/**
 * InventoryReportsPage.tsx
 *
 * Reports dashboard for inventory: Valuation, Usage Trends, Shrinkage, and Alerts.
 * Each tab lazily fetches its own data via React Query.
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getValuationReport,
  getUsageTrendsReport,
  getShrinkageReport,
  listLowStockProducts,
  type InventoryValuation,
  type UsageTrendEntry,
  type ShrinkageEntry,
  type Product,
} from '@/services/inventory.service';
import StockStatusBadge from './StockStatusBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { DatePicker } from '@/components/ui/DatePicker';

// ─── Constants ─────────────────────────────────────────────────────────────────

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

type TabKey = 'valuation' | 'usage' | 'shrinkage' | 'alerts';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'valuation', label: 'Valuation' },
  { key: 'usage', label: 'Usage Trends' },
  { key: 'shrinkage', label: 'Shrinkage' },
  { key: 'alerts', label: 'Alerts' },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatCurrency(value: number): string {
  return currencyFmt.format(value);
}

/** Default date range: 30 days ago to today (YYYY-MM-DD). */
function defaultDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

// ─── Shared UI pieces ──────────────────────────────────────────────────────────

const thCls = 'px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider';
const tdCls = 'px-4 py-3 text-sm text-slate-700 whitespace-nowrap';

function LoadingState() {
  return <p className="py-12 text-center text-sm text-slate-400">Loading report data...</p>;
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="py-12 text-center text-sm text-slate-400">{message}</p>;
}

// ─── Valuation Tab ─────────────────────────────────────────────────────────────

function ValuationTab() {
  const { data, isLoading, error } = useQuery<InventoryValuation[]>({
    queryKey: ['inventory-report-valuation'],
    queryFn: getValuationReport,
    staleTime: 5 * 60 * 1000,
  });

  const safeData = Array.isArray(data) ? data : [];

  const totals = useMemo(() => {
    if (safeData.length === 0) return null;
    return {
      totalRetail: safeData.reduce((sum, r) => sum + (r.totalRetailValue ?? 0), 0),
      totalUnits: safeData.reduce((sum, r) => sum + (r.totalUnits ?? 0), 0),
      categories: safeData.length,
      belowZeroCount: safeData.reduce((sum, r) => sum + (r.belowZeroCount ?? 0), 0),
      unitsShort: Math.abs(safeData.reduce((sum, r) => sum + (r.belowZeroUnits ?? 0), 0)),
    };
  }, [safeData]);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState message="Failed to load valuation report." />;
  if (safeData.length === 0) return <EmptyState message="No valuation data available." />;

  const chartData = safeData.map((row) => ({
    category: row.category ?? 'Uncategorized',
    retailValue: row.totalRetailValue,
  }));

  return (
    <div className="space-y-6">
      {/* Summary cards. No cost or margin: quote_item_costs has no cost-basis
          column, so those figures had no data source and were always zero.
          Value and units count stock above zero only; Below Zero says how many
          products were left out and by how much (SCRUM-1981). */}
      {totals && (
        <MetricCardGrid columns={4}>
          <StatCard label="Total Retail Value" value={formatCurrency(totals.totalRetail)} tone="blue" />
          <StatCard label="Units On Hand" value={totals.totalUnits.toLocaleString()} tone="violet" />
          <StatCard label="Categories" value={totals.categories} tone="teal" />
          <StatCard
            label="Below Zero"
            value={totals.belowZeroCount.toLocaleString()}
            subtitle={
              totals.belowZeroCount > 0
                ? `${totals.unitsShort.toLocaleString()} units short — not counted in value`
                : undefined
            }
            tone="amber"
          />
        </MetricCardGrid>
      )}

      {/* Bar chart */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Retail Value by Category</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="category" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v: number) => formatCurrency(v)} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="retailValue" name="Retail Value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className={thCls}>Category</th>
                <th className={`${thCls} text-right`}># Products</th>
                <th className={`${thCls} text-right`}>Units On Hand</th>
                <th className={`${thCls} text-right`}>Below Zero</th>
                <th className={`${thCls} text-right`}>Retail Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {safeData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className={tdCls}>{row.category ?? 'Uncategorized'}</td>
                  <td className={`${tdCls} text-right`}>{row.productCount}</td>
                  <td className={`${tdCls} text-right`}>{row.totalUnits.toLocaleString()}</td>
                  <td className={`${tdCls} text-right ${row.belowZeroCount > 0 ? 'text-amber-700' : ''}`}>
                    {row.belowZeroCount}
                  </td>
                  <td className={`${tdCls} text-right font-medium`}>{formatCurrency(row.totalRetailValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Usage Trends Tab ──────────────────────────────────────────────────────────

function UsageTrendsTab() {
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const { data, isLoading, error } = useQuery<UsageTrendEntry[]>({
    queryKey: ['inventory-report-usage', startDate, endDate, groupBy],
    queryFn: () => getUsageTrendsReport({ startDate, endDate, groupBy }),
    staleTime: 5 * 60 * 1000,
  });

  const safeData = Array.isArray(data) ? data : [];

  const summary = useMemo(() => {
    if (safeData.length === 0) return null;
    const totalAdded = safeData.reduce((sum, e) => sum + (e.totalAdded ?? 0), 0);
    const totalRemoved = safeData.reduce((sum, e) => sum + (e.totalRemoved ?? 0), 0);
    return { totalAdded, totalRemoved, netChange: totalAdded - totalRemoved };
  }, [safeData]);

  const inputCls =
    'border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="inventory-usage-start-date" className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
          <DatePicker
            id="inventory-usage-start-date"
            value={startDate}
            onChange={setStartDate}
            wrapperClassName="w-44"
          />
        </div>
        <div>
          <label htmlFor="inventory-usage-end-date" className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
          <DatePicker
            id="inventory-usage-end-date"
            value={endDate}
            onChange={setEndDate}
            wrapperClassName="w-44"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Group By</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
            className={inputCls}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>
      </div>

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Failed to load usage trends." />}

      {!isLoading && safeData.length === 0 && <EmptyState message="No usage data for the selected period." />}

      {safeData.length > 0 && (
        <>
          {/* Summary */}
          {summary && (
            <MetricCardGrid columns={3}>
              <StatCard label="Total Added" value={summary.totalAdded} tone="emerald" />
              <StatCard label="Total Removed" value={summary.totalRemoved} tone="amber" />
              <StatCard
                label="Net Change"
                value={`${summary.netChange >= 0 ? '+' : ''}${summary.netChange.toLocaleString()}`}
                tone={summary.netChange >= 0 ? 'green' : 'red'}
              />
            </MetricCardGrid>
          )}

          {/* Chart */}
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Added vs Removed Over Time</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="totalAdded" name="Added" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                <Bar dataKey="totalRemoved" name="Removed" fill={CHART_COLORS[3]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shrinkage Tab ─────────────────────────────────────────────────────────────

function ShrinkageTab() {
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);

  const { data: rawData, isLoading, error } = useQuery<ShrinkageEntry[]>({
    queryKey: ['inventory-report-shrinkage', startDate, endDate],
    queryFn: () => getShrinkageReport({ startDate, endDate }),
    staleTime: 5 * 60 * 1000,
  });
  const data = Array.isArray(rawData) ? rawData : [];

  const top5 = useMemo(() => {
    if (!data) return [];
    return [...data]
      .sort((a, b) => b.totalShrinkage - a.totalShrinkage)
      .slice(0, 5)
      .map((entry) => ({
        name: entry.productName,
        value: entry.totalShrinkage,
      }));
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="inventory-shrinkage-start-date" className="block text-xs font-medium text-slate-600 mb-1">Start Date</label>
          <DatePicker
            id="inventory-shrinkage-start-date"
            value={startDate}
            onChange={setStartDate}
            wrapperClassName="w-44"
          />
        </div>
        <div>
          <label htmlFor="inventory-shrinkage-end-date" className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
          <DatePicker
            id="inventory-shrinkage-end-date"
            value={endDate}
            onChange={setEndDate}
            wrapperClassName="w-44"
          />
        </div>
      </div>

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Failed to load shrinkage report." />}

      {!isLoading && data.length === 0 && <EmptyState message="No shrinkage data for the selected period." />}

      {data.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Table */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className={thCls}>Product Name</th>
                    <th className={`${thCls} text-right`}>Total Shrinkage</th>
                    <th className={`${thCls} text-right`}># Adjustments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.map((row) => (
                    <tr key={row.productId} className="hover:bg-slate-50">
                      <td className={tdCls}>{row.productName}</td>
                      <td className={`${tdCls} text-right font-medium text-red-700`}>
                        {row.totalShrinkage.toLocaleString()}
                      </td>
                      <td className={`${tdCls} text-right`}>{row.adjustmentCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pie chart */}
          {top5.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Top 5 Products by Shrinkage</h3>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={top5}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    innerRadius={50}
                    paddingAngle={2}
                    label={({ name, percent }: { name: string; percent: number }) =>
                      `${name.length > 18 ? name.slice(0, 18) + '...' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine
                  >
                    {top5.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Alerts Tab ────────────────────────────────────────────────────────────────

function AlertsTab() {
  const {
    data: attention,
    isLoading,
    error,
  } = useQuery<Product[]>({
    queryKey: ['inventory-low-stock'],
    // Wrapped, not passed bare: React Query calls queryFn with its context
    // object, which axios would then serialize into the query string.
    queryFn: () => listLowStockProducts(),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-base font-semibold text-slate-800 mb-3">Stock Needing Attention</h3>
        {isLoading && <LoadingState />}
        {error && <ErrorState message="Failed to load stock alerts." />}
        {attention && attention.length === 0 && (
          <EmptyState message="Nothing out of stock or below its reorder level." />
        )}
        {attention && attention.length > 0 && (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className={thCls}>Product Name</th>
                    <th className={`${thCls} text-right`}>Current Stock</th>
                    <th className={`${thCls} text-right`}>Alert Level</th>
                    <th className={thCls}>Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {attention.map((product) => (
                    <tr key={product.productId} className="hover:bg-slate-50">
                      <td className={tdCls}>{product.name}</td>
                      <td
                        className={`${tdCls} text-right font-medium ${
                          product.stockQuantity <= 0 ? 'text-red-700' : ''
                        }`}
                      >
                        {product.stockQuantity.toLocaleString()}
                      </td>
                      <td className={`${tdCls} text-right`}>
                        {product.lowStockAlert > 0 ? product.lowStockAlert.toLocaleString() : '—'}
                      </td>
                      <td className={tdCls}>
                        {/* Same server-computed status the stock list renders, so
                            the two surfaces cannot drift apart again. */}
                        <StockStatusBadge status={product.stockStatus} showOk />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Main Page Component ───────────────────────────────────────────────────────

export default function InventoryReportsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('valuation');

  return (
    <div className="p-6">
      <PageHeader title="Inventory Reports" />

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors -mb-px ${
              activeTab === tab.key
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'valuation' && <ValuationTab />}
      {activeTab === 'usage' && <UsageTrendsTab />}
      {activeTab === 'shrinkage' && <ShrinkageTab />}
      {activeTab === 'alerts' && <AlertsTab />}
    </div>
  );
}
